import { RowState } from './dataset.js';
import { displayDate, displayDateTime } from './format.js';
import { applyMask, regexMaskOf, regexPrefixOk, regexClip } from './editors.js';
import { trapTab, MAX_SCROLL_PX, nextDlgSeq } from './grid-util.js';
import { normValidation } from './input.js';
export class GridEditForm {
    /** @internal */ formMode() { const m = this.opts.editMode; return m === 'inlineForm' ? 'inline' : m === 'modalForm' ? 'modal' : null; }
    /** 편집 폼이 열려 있나 */
    isEditFormOpen() { return !!this.form; }
    /**
     * 편집 폼 열기 (QuantumGrid EditForm). editMode 가 inlineForm 이면 행 아래에 펼치고, 아니면 대화상자.
     * 칸마다 그리드와 같은 편집기 규칙(읽기 전용·필수·검증·콤보 목록·마스크). 저장 = Enter·Ctrl+Enter, 취소 = Esc
     */
    showEditForm(row = this.getFocusedRow(), opts = {}) {
        if (row < 0 || row >= this.ds.length || !this.ds.isVisibleRow(row) || !this.opts.editable)
            return false;
        if (this.form) {
            if (this.form.row === row) {
                this.focusFormField(opts.focusCol);
                return true;
            }
            if (!this.hideEditForm(true))
                return false;
        }
        if (this.editing && !this.commitEdit())
            return false;
        const mode = opts.mode ?? this.formMode() ?? 'modal';
        const ef = this.opts.editForm ?? {};
        const cols = (ef.columns?.length ? ef.columns.map(id => this.cols.find(c => c.id === id) ?? this.colInfo(id)) : this.cols).filter((c) => !!c && !c.special && this.columnAccess(c) !== 'hidden');
        const n = Math.max(1, Math.min(4, ef.colCount ?? 2)), seq = nextDlgSeq(), idp = 'og-ef' + seq;
        const el = document.createElement('div');
        el.className = 'og-efm og-efm-' + mode;
        el.setAttribute('role', mode === 'modal' ? 'dialog' : 'group');
        const vi = this.disp.indexOf(row), no = vi >= 0 ? (this.rowNo ? this.rowNo[vi] : vi) + 1 : row + 1;
        const h = document.createElement('div');
        h.className = 'og-efh';
        h.id = idp + 'h';
        h.textContent = (ef.title ?? this.t('행 편집')) + ` — ${this.t('행 {n}', { n: no })}${this.ds.rowState(row) === RowState.INSERT ? ' (' + this.t('새 행') + ')' : ''}`;
        el.setAttribute('aria-labelledby', h.id);
        if (mode === 'modal')
            el.setAttribute('aria-modal', 'true');
        const g = document.createElement('div');
        g.className = 'og-efg';
        g.style.gridTemplateColumns = `repeat(${n}, minmax(0, 1fr))`;
        const fields = [];
        cols.forEach((c, i) => {
            const kind = this.editorOf(c, row), can = !!kind && this.canEditCol(c, row), req = !!(this.ruleOf(c, row)?.required ?? c.def.required);
            const wrap = document.createElement('div');
            wrap.className = 'og-eff' + (kind === 'textarea' ? ' og-effw' : '');
            const lab = document.createElement('label');
            lab.className = 'og-efl';
            lab.htmlFor = idp + 'f' + i;
            lab.textContent = c.def.header ?? c.id;
            if (req && can) {
                const em = document.createElement('em');
                em.textContent = ' *';
                em.setAttribute('aria-hidden', 'true');
                lab.appendChild(em);
            }
            const v = this.valueOf(c, row);
            let input;
            if (can && (kind === 'combo' && c.def.listStyle !== 'editList' || kind === 'check3')) {
                const sel = document.createElement('select');
                const add = (val, text) => { const o = document.createElement('option'); o.value = val; o.textContent = text; sel.appendChild(o); };
                if (kind === 'check3') {
                    const cv = this.checkValues3(c);
                    add('0', this.t('예'));
                    add('1', this.t('아니오'));
                    add('2', this.t('미정'));
                    sel.value = v === cv[0] || v === true ? '0' : v === cv[2] ? '2' : '1';
                }
                else {
                    add('', '');
                    for (const it of this.itemsOf(c, row))
                        add(it.value, c.def.comboShow === 'value' ? it.value : `${it.value} ${it.label}`);
                    sel.value = v == null ? '' : String(v);
                }
                input = sel;
            }
            else if (can && (kind === 'check' || kind === 'radio')) {
                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.checked = this.isChecked(c, v);
                input = cb;
            }
            else if (can && kind === 'textarea') {
                const ta = document.createElement('textarea');
                ta.rows = 3;
                ta.value = v == null ? '' : String(v);
                if (c.def.maxLength)
                    ta.maxLength = c.def.maxLength;
                input = ta;
            }
            else {
                const inp = document.createElement('input');
                inp.type = 'text';
                inp.autocomplete = 'off';
                const sm = typeof c.def.mask === 'string' ? c.def.mask : null, rm = regexMaskOf(c.def.mask);
                inp.value = !can ? this.displayText(c, v, row) : v == null ? '' : kind === 'combo' ? String(v) : kind === 'date' ? (c.def.dateKind === 'datetime' ? displayDateTime(String(v)) : displayDate(String(v))) : sm ? applyMask(String(v), sm) : String(v);
                if (!can) {
                    inp.readOnly = true;
                    inp.setAttribute('aria-readonly', 'true');
                    inp.tabIndex = -1;
                }
                else {
                    if (kind === 'number' || kind === 'spin')
                        inp.inputMode = 'decimal';
                    if (kind === 'date')
                        inp.placeholder = c.def.dateKind === 'datetime' ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';
                    if (kind === 'time')
                        inp.placeholder = 'HH:mm';
                    if (rm?.placeholder)
                        inp.placeholder = rm.placeholder;
                    if (c.def.maxLength)
                        inp.maxLength = c.def.maxLength;
                    if (kind === 'combo') {
                        const dl = document.createElement('datalist');
                        dl.id = idp + 'l' + i;
                        for (const it of this.itemsOf(c, row)) {
                            const o = document.createElement('option');
                            o.value = it.value;
                            o.label = it.label;
                            dl.appendChild(o);
                        }
                        wrap.appendChild(dl);
                        inp.setAttribute('list', dl.id);
                    }
                    let good = inp.value;
                    inp.addEventListener('input', () => {
                        if (sm) {
                            const m = applyMask(inp.value, sm);
                            if (m !== inp.value)
                                inp.value = m;
                        }
                        else if (rm && !inp.__composing) {
                            if (regexPrefixOk(rm, inp.value))
                                good = inp.value;
                            else
                                inp.value = good = regexClip(rm, inp.value).startsWith(good) ? regexClip(rm, inp.value) : good;
                        }
                    });
                    inp.addEventListener('compositionstart', () => { inp.__composing = true; });
                    inp.addEventListener('compositionend', () => { inp.__composing = false; inp.dispatchEvent(new Event('input')); });
                }
                input = inp;
            }
            input.id = idp + 'f' + i;
            input.className = 'og-efi';
            input.dataset.col = c.id;
            if (req && can)
                input.setAttribute('aria-required', 'true');
            const err = document.createElement('div');
            err.className = 'og-efe';
            err.id = idp + 'e' + i;
            err.setAttribute('aria-live', 'polite');
            input.setAttribute('aria-describedby', err.id);
            wrap.append(lab, input, err);
            g.appendChild(wrap);
            fields.push({ c, kind: can ? kind : null, input, err });
        });
        const f = document.createElement('div');
        f.className = 'og-eft';
        const ok = document.createElement('button');
        ok.type = 'button';
        ok.className = 'og-rok';
        ok.textContent = this.t('저장');
        ok.dataset.a = 'save';
        const no2 = document.createElement('button');
        no2.type = 'button';
        no2.textContent = this.t('취소');
        no2.dataset.a = 'cancel';
        const hint = document.createElement('span');
        hint.className = 'og-efk';
        hint.textContent = this.t('Enter 저장 · Esc 취소');
        f.append(ok, no2, hint);
        el.append(h, g, f);
        f.addEventListener('click', e => { const a = e.target.closest('button')?.dataset.a; if (a === 'save')
            this.hideEditForm(true);
        else if (a === 'cancel')
            this.hideEditForm(false); });
        el.addEventListener('keydown', e => {
            e.stopPropagation();
            if (e.isComposing || e.keyCode === 229)
                return;
            const t = e.target;
            if (e.key === 'Escape') {
                e.preventDefault();
                this.hideEditForm(false);
            }
            else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey || (t.tagName !== 'TEXTAREA' && t.tagName !== 'BUTTON' && !e.shiftKey && !e.altKey))) {
                e.preventDefault();
                this.hideEditForm(true);
            }
            else if (e.key === 'Tab' && mode === 'modal')
                trapTab(el, e);
        });
        el.addEventListener('mousedown', e => e.stopPropagation());
        const st = { row, mode, el, h: 0, fields };
        this.form = st;
        if (mode === 'modal') {
            const back = document.createElement('div');
            back.className = 'og-dlgb og-efb';
            el.classList.add('og-dlg');
            back.appendChild(el);
            this.root.appendChild(back);
            st.back = back;
        }
        else {
            el.classList.add('og-dd');
            // 보이는 폭으로 먼저 놓고 내용 높이(머리·칸·단추 줄 합)를 잼 — 슬롯 높이를 정하면 상자가 늘어나므로 상자 자체 높이는 쓰지 않음
            const main = this.bodyPanes[1];
            this.detailLayer.style.width = this.paneWidth[0] + main.clientWidth + this.paneWidth[2] + 'px';
            this.detailLayer.appendChild(el);
            const natural = () => Math.ceil(h.offsetHeight + g.offsetHeight + f.offsetHeight + 4);
            st.h = Math.max(this.rh * 2, natural());
            if (typeof ResizeObserver === 'function') {
                st.ro = new ResizeObserver(() => { if (this.form !== st)
                    return; const nh = Math.max(this.rh * 2, natural()); if (Math.abs(nh - st.h) > 1) {
                    st.h = nh;
                    this.buildOffsets();
                    this.sizer.style.height = Math.min(this.totalH(), MAX_SCROLL_PX) + 'px';
                    this.pool.forEach(p => (p.key = ''));
                    this.requestRender(true);
                } });
                st.ro.observe(g);
            }
            this.refreshDisp();
            const v2 = this.disp.indexOf(row);
            if (v2 >= 0) { // 행과 폼이 보이게 (폼이 길면 행이 위에 오도록)
                this.ensureVisible(v2, this.focus.ci);
                const vh = main.clientHeight, end = this.rowTop(v2 + 1) + st.h;
                if (end > main.scrollTop + vh && this.totalH() <= MAX_SCROLL_PX)
                    main.scrollTop = Math.min(this.rowTop(v2), end - vh);
            }
            this.requestRender(true);
        }
        this.focusFormField(opts.focusCol, opts.initialText);
        if (this.opts.showIndicator)
            this.bump();
        return true;
    }
    /** @internal */ focusFormField(col, initialText) {
        const st = this.form;
        if (!st)
            return;
        const fl = st.fields.find(x => x.kind && x.c.id === col) ?? st.fields.find(x => x.kind);
        const inp = fl?.input;
        if (!inp) {
            st.el.querySelector('button.og-rok')?.focus();
            return;
        }
        inp.focus({ preventScroll: true });
        if (initialText != null && fl) {
            if (inp instanceof HTMLSelectElement) {
                const q = initialText.toLowerCase();
                const o = [...inp.options].find(x => x.value && (x.value.toLowerCase().startsWith(q) || x.text.toLowerCase().includes(q)));
                if (o)
                    inp.value = o.value;
            }
            else if (inp instanceof HTMLInputElement && inp.type === 'checkbox') { /* 글자는 버림 */ }
            else {
                inp.value = initialText;
                inp.dispatchEvent(new Event('input'));
                const L = inp.value.length;
                inp.setSelectionRange?.(L, L);
            }
        }
        else if (inp instanceof HTMLInputElement && inp.type === 'text')
            inp.select();
    }
    /** 폼 값 검사·반영. 오류가 있으면 칸에 표시하고 false */
    /** @internal */ saveForm() {
        const st = this.form, row = st.row, changes = [];
        let first = null;
        for (const fl of st.fields) {
            const { c, kind, input, err } = fl;
            if (!kind || !input)
                continue;
            let value, msg = null;
            if (input instanceof HTMLSelectElement)
                value = kind === 'check3' ? this.checkValues3(c)[+input.value] : input.value === '' ? null : input.value;
            else if (input instanceof HTMLInputElement && input.type === 'checkbox')
                value = this.checkValues(c)[input.checked ? 0 : 1];
            else {
                const p = this.parseInput(c, input.value, kind === 'button' ? 'text' : kind, row);
                if ('error' in p) {
                    msg = p.error;
                    value = null;
                }
                else
                    value = p.value;
            }
            if (!msg && (this.ruleOf(c, row)?.required ?? c.def.required) && (value == null || value === ''))
                msg = `${c.def.header ?? c.id}은(는) 필수입니다`;
            if (!msg)
                msg = this.bytesError(c, value);
            // 검증 수준: 오류만 막음, 경고·안내는 칸 아래 글로 보이고 저장 (15차)
            let warn = null;
            if (!msg && c.def.validate) {
                const m = normValidation(c.def.validate(value, row, this.ds));
                if (m?.level === 'error')
                    msg = m.message;
                else if (m) {
                    warn = m.message;
                    this.warns.set(row + ':' + c.id, m);
                }
            }
            err.textContent = msg ?? warn ?? '';
            err.classList.toggle('og-efw', !msg && !!warn);
            if (msg) {
                input.setAttribute('aria-invalid', 'true');
                first ?? (first = input);
                continue;
            }
            input.removeAttribute('aria-invalid');
            const old = this.valueOf(c, row);
            if (!(old === value || (old == null && value == null) || (old != null && value != null && String(old) === String(value) && typeof old === typeof value)))
                changes.push({ row, col: c.id, value });
        }
        if (first) {
            first.focus();
            return false;
        }
        if (changes.length) {
            const keep = st.fields.map(f => [f.c.id, this.warns.get(row + ':' + f.c.id)]).filter(x => x[1]);
            this.setCellByUser(changes, this.t('폼 편집'), 'edit', { noBuffer: true });
            for (const [col, w] of keep)
                this.warns.set(row + ':' + col, w);
        }
        return true;
    }
    /** 편집 폼 닫기. commit = 저장(검증 실패면 열어 둔 채 false), false = 취소 */
    hideEditForm(commit = true) {
        const st = this.form;
        if (!st)
            return true;
        if (commit && !this.saveForm())
            return false;
        this.closeForm();
        this.focusGrid();
        return true;
    }
    /** @internal */ closeForm() {
        const st = this.form;
        if (!st)
            return;
        this.form = null;
        st.ro?.disconnect();
        if (st.back)
            st.back.remove();
        else
            st.el.remove();
        if (st.mode === 'inline')
            this.refreshDisp();
        if (this.opts.showIndicator)
            this.bump();
    }
}
